import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LayoutDashboard, Sparkles, Copy, ExternalLink, Save } from "lucide-react";
import OwnerCampaignsSection from "@/components/OwnerCampaignsSection";

const NAV = (base) => [
  { to: base, label: "Panel", icon: LayoutDashboard, end: true },
  { to: `${base}/landing`, label: "Editor Landing", icon: Sparkles },
];

export default function InfluencerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = () => api.get("/influencer/dashboard").then((r) => setData(r.data)).catch((er) => toast.error(formatApiError(er.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  if (!data) {
    return <DashboardLayout title="Influencer" navItems={NAV("/app/influencer")}>
      <p className="text-gray-400">Cargando…</p>
    </DashboardLayout>;
  }
  const { influencer, codes, commissions, recent_events } = data;
  const landingUrl = `${window.location.origin}/i/${influencer.landing_slug}`;

  return (
    <DashboardLayout title={`Influencer · ${influencer.name}`} navItems={NAV("/app/influencer")}>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">{influencer.platform}</p>
          <h1 className="mt-1 text-3xl font-bold text-white font-display" data-testid="inf-title">{influencer.name}</h1>
          <p className="text-sm text-gray-400 mt-1">
            {influencer.handle} · {influencer.follower_range} · comisión {influencer.commission_pct}%
          </p>
        </div>
        <a href={landingUrl} target="_blank" rel="noreferrer"
          data-testid="inf-landing-link"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] text-sm font-semibold transition-all hover:scale-[1.02]">
          Ver mi landing <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-8">
        <Kpi label="Pendiente" value={commissions.total_pending_mxn} tone="orange" testid="kpi-pending" />
        <Kpi label="Pagado" value={commissions.total_paid_mxn} tone="green" testid="kpi-paid" />
        <Kpi label="All-time" value={commissions.total_all_time_mxn} tone="gray" testid="kpi-all" />
      </div>

      <section className="mb-8">
        <h2 className="text-lg font-bold text-white mb-3">Mis códigos</h2>
        <div className="grid md:grid-cols-2 gap-3">
          {codes.map((c) => (
            <article key={c.id} data-testid={`code-card-${c.code}`} className="p-4 rounded-2xl bg-white/5 border border-white/10">
              <p className="font-mono text-lg font-bold text-[#F26A21]">{c.code}</p>
              <p className="text-xs text-gray-400 mt-1">{c.used_count} usos</p>
              <div className="mt-3 flex items-center gap-2 text-xs">
                <input readOnly value={`${landingUrl}?ref=${c.code}`}
                  className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1 text-gray-300 font-mono" />
                <button onClick={() => { navigator.clipboard.writeText(`${landingUrl}?ref=${c.code}`); toast.success("Link copiado"); }}
                  className="p-1.5 bg-white/5 rounded hover:bg-white/10"><Copy className="w-3 h-3 text-white" /></button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-lg font-bold text-white mb-3">Comisiones recientes</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-widest text-gray-500">
              <tr><th className="py-2">Fecha</th><th>Venta</th><th>%</th><th>Comisión</th><th>Estado</th></tr>
            </thead>
            <tbody className="text-gray-300">
              {commissions.items.slice(0, 10).map((c) => (
                <tr key={c.id} className="border-t border-white/5" data-testid={`commission-row-${c.id}`}>
                  <td className="py-2 text-xs">{new Date(c.earned_at).toLocaleDateString()}</td>
                  <td className="font-mono">${c.sale_amount_mxn.toLocaleString()}</td>
                  <td>{c.commission_pct}%</td>
                  <td className="font-mono text-[#F26A21] font-bold">${c.commission_mxn.toLocaleString()}</td>
                  <td><StatusChip s={c.status} /></td>
                </tr>
              ))}
              {commissions.items.length === 0 && <tr><td colSpan={5} className="py-6 text-gray-500 text-center">Sin comisiones todavía.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <OwnerCampaignsSection ownerType="influencer" />
    </DashboardLayout>
  );
}

// Simple landing editor (hero + featured programs)
export function InfluencerLandingEditor() {
  const { user } = useAuth();
  const [inf, setInf] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [heroCopy, setHeroCopy] = useState("");
  const [featured, setFeatured] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/influencer/me"),
      api.get("/catalog/programs?limit=200"),
    ]).then(([m, p]) => {
      setInf(m.data);
      setHeroCopy(m.data.hero_copy || "");
      setFeatured(m.data.featured_program_ids || []);
      setPrograms(p.data.items);
    }).catch(() => toast.error("Error cargando editor"));
  }, []);

  const toggle = (id) => {
    setFeatured((f) => {
      if (f.includes(id)) return f.filter((x) => x !== id);
      if (f.length >= 6) { toast.error("Máximo 6 programas destacados"); return f; }
      return [...f, id];
    });
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/influencer/landing", { hero_copy: heroCopy, featured_program_ids: featured });
      toast.success("Landing guardada");
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
    setSaving(false);
  };
  if (!inf) return <DashboardLayout title="Landing editor" navItems={NAV("/app/influencer")}><p className="text-gray-400">Cargando…</p></DashboardLayout>;

  return (
    <DashboardLayout title="Editor de landing" navItems={NAV("/app/influencer")}>
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Fase 7 · Editor</p>
        <h1 className="mt-1 text-3xl font-bold text-white font-display">Personaliza tu landing</h1>
        <p className="text-sm text-gray-400 mt-1">Se muestra en /i/{inf.landing_slug}</p>
      </div>
      <label className="block mb-6">
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Hero copy · tu pitch</span>
        <textarea rows={3} value={heroCopy} onChange={(e) => setHeroCopy(e.target.value)}
          data-testid="landing-hero-copy"
          className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F26A21]" />
      </label>
      <p className="text-sm text-gray-400 mb-3">Programas destacados ({featured.length}/6):</p>
      <div className="grid md:grid-cols-3 gap-2 max-h-[400px] overflow-y-auto p-1">
        {programs.map((p) => {
          const active = featured.includes(p.id);
          return (
            <button key={p.id} onClick={() => toggle(p.id)}
              data-testid={`landing-toggle-${p.code}`}
              className={`text-left p-3 rounded-lg border text-xs transition-all ${active ? "bg-[#F26A21] text-[#0B0B0B] border-[#F26A21]" : "bg-white/5 border-white/10 text-gray-300 hover:border-[#F26A21]"}`}>
              <p className="font-mono text-[9px] font-bold opacity-70">{p.code}</p>
              <p className="mt-1 font-semibold leading-tight">{p.title}</p>
            </button>
          );
        })}
      </div>
      <button onClick={save} disabled={saving}
        data-testid="landing-save-btn"
        className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm transition-all hover:scale-[1.02]">
        <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar landing"}
      </button>
    </DashboardLayout>
  );
}

function Kpi({ label, value, tone, testid }) {
  const toneCls = {
    orange: "text-[#F26A21] border-[#F26A21]/40",
    green: "text-emerald-400 border-emerald-500/40",
    gray: "text-white border-white/20",
  }[tone];
  return (
    <div data-testid={testid} className={`p-4 rounded-2xl bg-white/5 border ${toneCls}`}>
      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{label}</p>
      <p className={`mt-1 text-2xl font-extrabold font-mono ${toneCls.split(" ")[0]}`}>${(value || 0).toLocaleString()}</p>
    </div>
  );
}

function StatusChip({ s }) {
  const tone = {
    pending: "bg-amber-500/15 text-amber-300",
    approved: "bg-sky-500/15 text-sky-300",
    paid: "bg-emerald-500/15 text-emerald-300",
    cancelled: "bg-red-500/15 text-red-300",
  }[s] || "bg-white/5 text-gray-300";
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${tone}`}>{s}</span>;
}
